// Shared pieces of the Inventory section: the photo, owner, cost and QA chips every tab shows.
import { consignorName, money } from '../format'
import { Badge } from '../ui'

export interface FloorPiece {
  product_id: string; variant_id: string; title: string; brand: string; product_type: string
  size: string | null; sku: string; barcode: string; image_url: string | null; quantity: number
  list_price: number; floor: number | null; owner: string | null; owner_source: string; consignor_key: string | null
  cost: number | null; cost_source: string; shopify_cost: number | null; acquisition: string | null; intake_no: number | null
  on_floor_since: string; days_on_floor: number; qa: string
}

/** A Shopify CDN photo at the size the view needs; the CDN resizes with ?width=. */
export const withWidth = (url: string, w: number) => url.includes('?') ? `${url}&width=${w}` : `${url}?width=${w}`

export function PhotoImg({ url, title, width, small }: { url: string | null; title: string; width: number; small?: boolean }) {
  if (!url) return <span className={'rp-photo rp-photo--none' + (small ? ' rp-photo--small' : '')} aria-label="No photo"><svg width={small ? 18 : 28} height={small ? 18 : 28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h16v14H4zM4 15l5-5 4 4 3-3 4 4" /></svg>{!small && <span>No photo</span>}</span>
  return <img className={'rp-photo' + (small ? ' rp-photo--small' : '')} src={withWidth(url, width)} alt={title} loading="lazy" />
}

export function OwnerChip({ owner, consignorKey, extra }: { owner: string | null; consignorKey?: string | null; extra?: string }) {
  const label = owner === 'consignor' ? (consignorKey ? consignorName(consignorKey) : 'Consignor') : owner ? owner[0].toUpperCase() + owner.slice(1) : 'Not recorded'
  return <Badge tone={owner ? 'plain' : 'red'}>{label}{extra ? ` · ${extra}` : ''}</Badge>
}

/** Cost with its source: intake / trade credit (real), consignor (none by rule), typed (a Shopify unit cost on a legacy piece,
 *  shown but not counted), legacy (unknown by design; never a guess). */
export function CostChip({ source, cost, shopifyCost }: { source: string; cost: number | null; shopifyCost?: number | null }) {
  if (source === 'intake') return <Badge tone="green">Cost {money(cost, true)} · intake</Badge>
  if (source === 'trade_credit') return <Badge tone="green">Cost {money(cost, true)} · trade</Badge>
  if (source === 'consignor') return <Badge tone="grey">No cost · consignor</Badge>
  if (source === 'typed') return <Badge tone="amber">{money(cost ?? shopifyCost, true)} typed in Shopify</Badge>
  return <Badge tone="grey">Cost unknown · legacy</Badge>
}

export function QaBadge({ qa }: { qa: string }) {
  if (qa === 'passed') return <Badge tone="green">QA passed</Badge>
  if (qa === 'sent_back') return <Badge tone="amber">Sent back</Badge>
  return <Badge tone="grey">Not checked</Badge>
}
